<template>
	<loading-el v-if="loading"></loading-el>
	<div class="edit-row edit-row--b-top edit-row--b-bottom dotted mod--edit-bar">
		<div class="flex flex--justify-between flex--grow">
			<div class="field-item field-item--grow">
				<div class="quick-control margin--none">
					<div
						v-for="option in NODE_EDIT_DESIGN_OPTIONS"
						:key="option.VALUE"
						@click="optionSet(option)"
						:class="{
							active: optionActive.VALUE === option.VALUE,
						}"
						class="quick-control__item"
					>
						<i :class="option.ICON"></i>
					</div>
				</div>
			</div>
			<div class="field-item field-item--grow flex--justify-end">
				<div class="field-label mr--none:desktop">{{ optionActive.TITLE }}</div>
			</div>
		</div>
	</div>
	
	<div
		class="edit-row--single mod--edit-bar"
	>
		<template v-if="optionActive.VALUE === NODE_EDIT_DESIGN_OPTIONS.SHAPE.VALUE">
			<div class="section-row mb--20">
				<shape-list
					v-model:shape-current="node.design.shape"
					class="shape-list--bar"
				></shape-list>
			</div>
			<div class="section-row mb--20">
				<transformation-block
					v-model:width="node.design.transformation.width"
					v-model:height="node.design.transformation.height"
					v-model:locked="node.design.transformation.locked"
					:unlockable="node.design.shape && node.design.shape !== NODE_SHAPES.CIRCLE.VALUE"
					:is-label="false"
				></transformation-block>
			</div>
		</template>
		
		<div
			v-if="optionActive.VALUE === NODE_EDIT_DESIGN_OPTIONS.DESIGN.VALUE"
			class="section-row section-row--double-colorpicker row-gap--20 mb--20"
		>
			<appearance-border
				v-model:enabled="node.design.border.value"
				:enabled-name="node.design.border.name"
				v-model:color="node.design.border.color"
				v-model:width="node.design.border.width"
				v-model:style="node.design.border.style"
			></appearance-border>
			
			<div class="row-item row-item--color">
				<field-checkbox
					v-model:checked="node.design.surface.value"
					:name="node.design.surface.name"
				></field-checkbox>
				<field-colorpicker
					v-model:color-value="node.design.surface.color"
					:transparency="true"
				></field-colorpicker>
			</div>
			<div class="row-item">
				<div class="field-row">
					<field-media
						v-model:image="node.design.media.local"
						v-model:imageURL="node.design.media.url"
            :label="t('field.appearance.media')"
						:show-url="false"
						:label-inline="true"
						@popup-open="popupMediaOpen"
					></field-media>
				</div>
			</div>
			<appearance-media-crop
				:disabled="!isMediaSettingsAvailable"
				v-model:enabled="node.design.crop.enabled"
				:enabled-name="node.design.crop.name"
				v-model:size="node.design.media.size"
				v-model:opacity="node.design.media.opacity"
			></appearance-media-crop>
		</div>
		
		<template
			v-if="optionActive.VALUE === NODE_EDIT_DESIGN_OPTIONS.FONT.VALUE"
		>
			<typography-font
				v-model:font-selected="node.typography.fontFamily"
				:font-options="fontOptions"
				v-model:weight-selected="node.typography.fontWeight"
				:weight-options="fontProperties"
				v-model:font-size="node.typography.fontSize"
				v-model:line-height="node.typography.lineHeight"
			></typography-font>
			
			<div class="section-row section-row--triple-min-auto mb--20">
				<div class="row-item row-item--color pl--6">
					<p class="color--white">{{ t('quickEditBar.fontColor') }}</p>
					<field-colorpicker v-model:color-value="node.typography.color"></field-colorpicker>
				</div>
				<div class="row-item pl--6">
					<align-list
						v-model:align-current="node.typography.align"
						:items="alignOptions"
						:disabled="isAlignmentDisabled"
					></align-list>
				</div>
				<div class="row-item">
					<div class="field-row">
						<field-select
							:label="t('field.typography.textPosition')"
							:label-inline="true"
							:options="textPositions"
							:by-value="'VALUE'"
							:property="'NAME'"
							v-model:selected="node.typography.positionSelected"
						></field-select>
					</div>
				</div>
			</div>
			
		</template>
		<div class="button-wrapper flex--justify-between">
			<div class="button-wrapper">
				<div
					:class="[isUpdateButtonAvailableClassname, hasDesignTemplatesClassname, isTemplateRelatedOccupied]"
					@click="templateUpdate"
					class="button button--plain button--h-25 mr--15"
				>
					<i v-if="!templateRelatedOccupied" class="fas fa-sync"></i>
					<div
						v-else
						:class="templateRelatedOccupied.colorName"
						class="map-user map-user--small"
					>{{ templateRelatedOccupied.initials }}</div>
					<span class="ml--15">{{ t('objectTemplate.update') }}</span>
				</div>
				
				<div class="button-wrapper rel">
					<div
						:class="[isCreateButtonAvailableClassname, hasDesignTemplatesClassname, isTemplateRelatedOccupied]"
						@click="templateCreateProceed"
						class="button button--plain button--h-25"
					>
						<i v-if="!templateRelatedOccupied" class="fas fa-pencil-ruler"></i>
						<div
							v-else
							:class="templateRelatedOccupied.colorName"
							class="map-user map-user--small"
						>{{ templateRelatedOccupied.initials }}</div>
						<span class="ml--15">{{ t('objectTemplate.createShort') }}</span>
					</div>
					<dialog-inline
						v-if="isTemplateCreating"
						:position="'start'"
						:direction="'top'"
						:active="isTemplateCreating"
						@confirm="templateCreate"
						@cancel="templateCreateCancel"
					>
						<div class="field-row">
							<field-regular
								:label-inline="true"
								:field-item-classname="'max-width--150 mb--15'"
								:focused="1"
								v-model:model-value="templateCreatedName"
								@emit-action="templateCreate"
							></field-regular>
						</div>
					</dialog-inline>
				</div>
			</div>
			<div
				@click="openDesignSettings"
				class="button button--plain button--h-25 bg--white color--black font-weight--500"
			>{{ t('quickEditBar.moreOptions') }}</div>
		</div>
	</div>
	<popup-media
		:popup-status="isPopupMediaActive"
		@chosen-media="mediaChosenGet"
		@popup-close="popupMediaClose"
	></popup-media>
</template>

<script>
import {ref, computed, reactive, inject} from 'vue';
import TransformationBlock from '@/components/editor-options/settings/sidebar/blocks/TransformationBlock';
import AppearanceMediaCrop from '@/components/editor-options/settings/sidebar/blocks/AppearanceMediaCrop';
import AppearanceBorder from '@/components/editor-options/settings/sidebar/blocks/AppearanceBorder';
import FieldColorpicker from '@/components/fields/FieldColorpicker';
import FieldSelect from '@/components/fields/FieldSelect';
import FieldRegular from '@/components/fields/FieldRegular';
import {SE_SYNC_TYPES} from '@/models/SE_SYNC_TYPES';
import {TEMPLATE_TYPES} from "@/models/TEMPLATE_TYPES";
import {useStore} from 'vuex';
import FontsUtils from '@/services/canvas/fonts/Fonts';
import { getNodeUpdatedData } from '@/modules/Node';
import {useI18n} from 'vue-i18n';
import FieldCheckbox from '@/components/fields/FieldCheckbox';
import FieldMedia from '@/components/fields/FieldMedia';
import AlignList from "@/components/editor-options/settings/sidebar/elements/AlignList";
import ShapeList from "@/components/editor-options/settings/sidebar/elements/ShapeList.vue";
import {DASHBOARD_OPTIONS} from '@/models/DASHBOARD_OPTIONS';
import {TEXT_POSITION} from "@/models/TEXT_POSITION";
import {TEXT_ALIGN} from "@/models/TEXT_ALIGN";
import {NODE_EDIT_DESIGN_OPTIONS} from "@/models/edit-bar/NODE_EDIT_DESIGN_OPTIONS";
import DialogInline from "@/components/ui/dialog/DialogInline.vue";
import LoadingEl from "@/components/ui/loading/LoadingEl.vue";
import useLoading from "@/utils/Loading";
import {NODE_SHAPES} from "@/models/NODE_SHAPES";
import TypographyFont from "@/components/editor-options/settings/sidebar/blocks/TypographyFont.vue";
import PopupMedia from "@/components/popup/PopupMedia.vue";
import {useFieldMedia} from "@/utils/media/useFieldMedia";

export default {
	components: {
		PopupMedia,
		LoadingEl,
		DialogInline,
		AppearanceBorder,
		AppearanceMediaCrop,
    AlignList,
		ShapeList,
		TransformationBlock,
		TypographyFont,
		FieldColorpicker,
		FieldSelect,
		FieldRegular,
		FieldCheckbox,
		FieldMedia
	},
	setup() {
		const {t} = useI18n()
		const store = useStore();
		const emitter = inject('emitter');
		
		// loading
		const {loading, loadingShow, loadingHide} = useLoading();
		
		const currentNode = computed(() => store.getters['nodes/getSelectedNode']);
		const currentNodeOriginal = computed(() => store.getters['nodes/getSelectedRawNode']);
		
		// STORE ACTIONS
		function setLockedFieldIgnoring(status){
			store.dispatch('nodes/setLockedFieldIgnoring', status);
		}
		function nodeUpdate(nodeData){
			const id = currentNode.value.id;
			store.dispatch('nodes/updateNode', {id, nodeData});
		}
		function nodeDesignUpdate(designData){
			const id = currentNode.value.id;
			store.dispatch('nodes/design/nodeDesignUpdate', {id, designData});
		}
		function nodeDesignSave(designData){
			const id = currentNode.value.id;
			store.dispatch('nodes/design/nodeDesignSave', {id, designData});
		}
		
		const NODE_DESIGN = DASHBOARD_OPTIONS.CREATE.GROUPS.NODE.NODE_DESIGN;
		async function openDesignSettings(){
			await store.dispatch("dashboardOptions/optionChoose", NODE_DESIGN);
			await emitter.emit('closeQuickEditBarPanel');
			
		}
		
		// tab OPTIONS
		const optionActive = ref(NODE_EDIT_DESIGN_OPTIONS.SHAPE);
		function optionSet(option){
			optionActive.value = option;
		}
		
		// utils
		// getNodeUpdatedData imported from @/modules/Node
		
		// TRANSFORMATION
		const isTemplateApplying = computed(() => store.getters["nodes/isTemplateApplying"]);
		const nodeWidth = computed({
			get(){return currentNode.value.width},
			set(value){
				if(isTemplateApplying.value) return;
				value = !value ? currentNode.value.width : value;
				// nodeUpdate({width: value});
				const nodeData = {};
				// if shape is one of that types where all the borders are equal : circle, square..
				// update both side with the same value
				if(currentNode.value.shape === NODE_SHAPES.CIRCLE.VALUE){
					Object.assign(nodeData, {width: value, height: value});
				} else {
					Object.assign(nodeData, {width: value});
				}
				nodeDesignUpdate(nodeData);
				nodeDesignSave(nodeData);
			}
		});
		const nodeHeight = computed({
			get(){return currentNode.value.height},
			set(value){
				if(isTemplateApplying.value) return;
				value = !value ? currentNode.value.height : value;
				nodeUpdate({height: value})
				const nodeData = {};
				// if shape is one of that types where all the borders are equal : circle, square..
				// update both side with the same value
				if(currentNode.value.shape === NODE_SHAPES.CIRCLE.VALUE){
					Object.assign(nodeData, {width: value, height: value});
				} else {
					Object.assign(nodeData, {height: value});
				}
				// if width and height fields are locked
				// node should be updated only once, by nodeWidth setter
				nodeDesignUpdate(nodeData);
				nodeDesignSave(nodeData);
			}
		});
		const nodeShape = computed({
			get(){return currentNode.value.shape || NODE_SHAPES.CIRCLE.VALUE},
			async set(value){
				// check if shape is equilateral
				setLockedFieldIgnoring(true);
				if(value === NODE_SHAPES.CIRCLE.VALUE && nodeWidth.value !== nodeHeight.value){
					const maxValue = Math.max(nodeWidth.value, nodeHeight.value);
					nodeUpdate({height: maxValue, width: maxValue})
				}
				nodeDesignUpdate({shape: value});
				await nodeDesignSave({shape: value});
				setLockedFieldIgnoring(false);
			}
		})
		
		// BG
		const nodeSurface = computed({
			get(){return currentNode.value.surface},
			set(value){
				nodeDesignUpdate({surface: value});
				nodeDesignSave({surface: value});
			}
		})
		const nodeBg = computed({
			get(){return currentNode.value.bgColor},
			set(value){
				nodeDesignUpdate({bgColor: value});
				nodeDesignSave({bgColor: value});
			}
		})
		
		// BORDER
		const nodeBorder = computed({
			get(){return currentNode.value.border},
			set(value){
				nodeDesignUpdate({border: value});
				nodeDesignSave({border: value});
			}
		})
		const nodeBorderWidth = computed({
			get(){return currentNode.value.borderWidth},
			set(value){
				nodeDesignUpdate({borderWidth: value});
				nodeDesignSave({borderWidth: value});
			}
		})
		const nodeBorderColor = computed({
			get(){return currentNode.value.borderColor},
			set(value){
				nodeDesignUpdate({borderColor: value});
				nodeDesignSave({borderColor: value});
			}
		})
		const nodeBorderStyle = computed({
			get(){return currentNode.value.borderStyle},
			set(value){
				nodeDesignUpdate({borderStyle: value});
				nodeDesignSave({borderStyle: value});
			}
		})
		
		// IMAGE
		const nodeMedia = computed({
			get(){return currentNode.value.media},
			async set(value){
				nodeDesignUpdate({media: value});
				nodeDesignSave({media: value});
			}
		})
		const nodeMediaURL = computed({
			get(){return currentNode.value.mediaURL},
			async set(value){
				nodeDesignUpdate({mediaURL: value});
				nodeDesignSave({mediaURL: value});
			}
		})
		const nodeMediaSize = computed({
			get(){return currentNode.value.mediaSize},
			set(value){
				nodeDesignUpdate({mediaSize: value});
				nodeDesignSave({mediaSize: value});
			}
		})
		const nodeMediaOpacity = computed({
			get(){return currentNode.value.mediaOpacity},
			set(value){
				nodeDesignUpdate({mediaOpacity: value});
				nodeDesignSave({mediaOpacity: value});
			}
		})
		const nodeMediaCropEnabled = computed({
			get(){return currentNode.value.mediaCropEnabled},
			set(value){
				nodeDesignUpdate({mediaCropEnabled: value});
				nodeDesignSave({mediaCropEnabled: value});
			}
		})
		const isMediaSettingsAvailable = computed(() => !!(currentNode.value.media || currentNode.value.mediaURL));
		
		// FONT
    const fonts = computed(() => store.getters['fonts/getFontsCollected']);
    const fontOptions = computed(() => fonts.value ? Object.values(fonts.value) : []);
    const font = computed(() => fonts.value[currentNode.value.fontFamily] || null);
    const fontProperties = computed(() => font.value ? Object.values(font.value.properties) : []);
    const fontProperty = computed(() => font.value ? fontProperties.value.filter(property =>
      (property.weight === currentNode.value.fontWeight) && (property.style === currentNode.value.fontStyle))[0] : null);
    // utils
		const {getFontProperty} = FontsUtils();
  
		const nodeFontFamily = computed({
			get(){
				return currentNode.value.fontFamily;
			},
			set(value){
				// update font weight alongside with font family changing
        if(currentNode.value.fontFamily === value) return;
        const {fontWeight, fontStyle} = getFontProperty({fonts, fontValue: value});
        nodeDesignUpdate({fontFamily: value, fontWeight, fontStyle});
        nodeDesignSave({fontFamily: value, fontWeight, fontStyle});
			}
		})
		
		const nodeFontWeight = computed({
			get(){return fontProperty.value},
			set(value){
        nodeDesignUpdate({fontWeight: value.weight, fontStyle: value.style});
        nodeDesignSave({fontWeight: value.weight, fontStyle: value.style});
			}
		})
		
		const nodeFontSize = computed({
			get(){return currentNode.value.fontSize},
			set(value){
				nodeDesignUpdate({fontSize: value});
				nodeDesignSave({fontSize: value});
			}
		})
		const nodeFontColor = computed({
			get(){return currentNode.value.fontColor},
			set(value){
				nodeDesignUpdate({fontColor: value});
				nodeDesignSave({fontColor: value});
			}
		})
		const nodeLineHeight = computed({
			get(){return currentNode.value.lineHeight},
			set(value){
				nodeDesignUpdate({lineHeight: value});
				nodeDesignSave({lineHeight: value});
			}
		})

    // text position
    const textPositions = Object.values(TEXT_POSITION).map(position => position);
    const titlePosition = computed({
      get(){return currentNode.value.titlePosition},
      set(value){
        setAlign(value);
        nodeDesignUpdate({titlePosition: value});
        nodeDesignSave({titlePosition: value});
      }
    })

    // text align
    const isAlignmentDisabled = computed(() => titlePosition.value !== TEXT_POSITION.CENTER.VALUE);
	  const alignOptions = [TEXT_ALIGN.LEFT.VALUE, TEXT_ALIGN.CENTER.VALUE, TEXT_ALIGN.RIGHT.VALUE];
    const textAlign = computed({
      get(){return currentNode.value.textAlign},
      set(value){
        nodeDesignUpdate({textAlign: value});
        nodeDesignSave({textAlign: value});
      }
    })
    function setAlign(value){
      switch (value){
        case TEXT_POSITION.LEFT.VALUE:
        case TEXT_POSITION.BOTTOM_LEFT.VALUE:
        case TEXT_POSITION.TOP_LEFT.VALUE: {
          textAlign.value = TEXT_ALIGN.RIGHT.VALUE;
          break;
        }
        case TEXT_POSITION.RIGHT.VALUE:
        case TEXT_POSITION.BOTTOM_RIGHT.VALUE:
        case TEXT_POSITION.TOP_RIGHT.VALUE: {
          textAlign.value = TEXT_ALIGN.LEFT.VALUE;
          break;
        }
        case TEXT_POSITION.TOP.VALUE:
        case TEXT_POSITION.BOTTOM.VALUE: {
          textAlign.value = TEXT_ALIGN.CENTER.VALUE;
          break;
        }
      }
    }

		const node = reactive({
			design: {
				shape: nodeShape,
				transformation: {
					width: nodeWidth,
					height: nodeHeight,
					locked: true,
					rotate: null
				},
				surface: {
          get name() { return t('field.appearance.color') },
					value: nodeSurface,
					color: nodeBg,
				},
				media: {
					local: nodeMedia,
					url: nodeMediaURL,
					size: nodeMediaSize,
					opacity: nodeMediaOpacity
				},
				crop: {
          get name() { return t('field.appearance.crop') },
					enabled: nodeMediaCropEnabled
				},
				border: {
					get name() { return t('field.appearance.border') },
					value: nodeBorder,
					color: nodeBorderColor,
					width: nodeBorderWidth,
					style: nodeBorderStyle,
				},
			},
			typography: {
				fontFamily: nodeFontFamily,
				fontWeight: nodeFontWeight,
				fontSize: nodeFontSize,
				lineHeight: nodeLineHeight,
				color: nodeFontColor,
        positionSelected: titlePosition,
        align: textAlign
			},
		});
		
		// POPUP MEDIA
		const {isPopupMediaActive, popupMediaOpen, popupMediaClose} = useFieldMedia();
		function mediaChosenGet(url){
			node.design.media.local = url;
			popupMediaClose();
		}
		
		// TEMPLATE
		const clientRestrictions = computed(() => store.getters["clients/getRestrictions"]);
		const hasDesignTemplates = computed(() => clientRestrictions.value.hasDesignTemplates.value);
		const hasDesignTemplatesClassname = computed(() => hasDesignTemplates.value ? "" : "disabled");
		const isTemplateChangesTracked = computed(() => store.getters["nodes/isTemplateChangesTracked"]);
		const isTemplateUpdateAvailable = computed(() =>
			isTemplateChangesTracked.value && currentNode.value.template && currentNode.value.template !== '0'
		);
		const isUpdateButtonAvailableClassname = computed(() => isTemplateUpdateAvailable.value ? "bg--pink" : "disabled bg--black");
		const isCreateButtonAvailableClassname = computed(() => isTemplateChangesTracked.value ? "bg--sea" : "disabled");
		
		// check if template is occupied
		const objectTemplates = computed(() => store.getters["templates/getObjectTemplates"]);
		const templateRelatedOccupied = computed(() => objectTemplates.value.find(template => template.id === parseInt(currentNode.value.template))?.occupied);
		const isTemplateRelatedOccupied = computed(() => templateRelatedOccupied.value ? "disabled bg--black" : "");
		
		// templates
		const nodeTemplates = computed(() => store.getters["design/getNodesTemplates"]);
		function getTemplate(templateId){
			let tmp = null;
			for(const [id, value] of Object.entries(nodeTemplates.value)){
				if(+id === +templateId) tmp = value
			}
			return tmp;
		}
		
		async function templateUpdate(){
			if(templateRelatedOccupied.value) return;
			if(!isTemplateUpdateAvailable.value) return;
			// exact fields which have been changed
			const updatedData = getNodeUpdatedData(currentNodeOriginal.value.design);
			const currentTemplateData = JSON.parse(JSON.stringify(getTemplate(currentNode.value.template)))
			const resultData = Object.assign(currentTemplateData, updatedData);
			const templateData = {
				id: parseInt(currentNode.value.template),
				data: resultData
			}
			
			// update template in the store
			await store.dispatch('design/templateUpdate', templateData);
			// send updated template data to the server
			await store.dispatch('design/templateSave', templateData);
			// update object template
			templateData.data.type = TEMPLATE_TYPES.NODE;
			objectTemplateUpdateByData(templateData);
			objectTemplatesUpdatedApplyToNodes({templateId: templateData.id});
			
			const nodeDesign = await nodeNullify(currentNode.value.id);

			await store.dispatch('nodes/updateNodeTemplate', {
				nodeId: currentNode.value.id,
				nodeData: nodeDesign,
				type: SE_SYNC_TYPES.OBJECT_TEMPLATES_NODE_UPDATED,
				template: templateData
			});
		}
		
		const isTemplateCreating = ref(false);
		const templateCreatedName = ref('');
		function templateCreateProceed(){
			isTemplateCreating.value = true;
		}
		function templateCreateCancel(){
			isTemplateCreating.value = false;
			templateCreatedName.value = '';
		}
		async function templateCreate(){
			if(!templateCreatedName.value) return;
			const templateObject = {name: templateCreatedName.value};
			const nodeId = currentNodeOriginal.value.id;
			Object.assign(templateObject, {type: 'node'});
			const template = await store.dispatch('design/templateCreate', {templateObject});
			
			loadingShow();
			await store.dispatch('nodes/populateCreatedNodeTemplate', {nodeId, template});
			
			await store.dispatch('nodes/saveNodeDesignByTemplate', {nodeId, nodeData: currentNodeOriginal.value.design});
			await store.dispatch('design/templateSave', {id: template.id});
			
			await store.dispatch('templates/objectTemplateCreate', {id: template.id});
			loadingHide();
			templateCreateCancel();
			// SE send event
			const editsData = {
				type: SE_SYNC_TYPES.NODE_TEMPLATE_CREATED,
				data: {nodeId, template}
			}
			await store.dispatch('simultaneousEditing/syncEdits', editsData);
		}
		
		
		
		// STORE ACTIONS
		function nodeNullify(nodeId){
			return store.dispatch('nodes/nullifyRawNode', nodeId);
		}
		function objectTemplatesUpdatedApplyToNodes(templateId){
			store.dispatch('nodes/applyUpdatedObjectTemplatesToNodes', templateId)
		}
		function objectTemplateUpdateByData({id, data}){
			store.dispatch('templates/objectTemplateUpdateByData', {id, data})
		}
		
		return {
			t,
			NODE_SHAPES,
			NODE_EDIT_DESIGN_OPTIONS,
			node,
			openDesignSettings,
			isMediaSettingsAvailable,
      fontOptions,
      fontProperties,
			isUpdateButtonAvailableClassname,
			isCreateButtonAvailableClassname,
			hasDesignTemplatesClassname,
			templateUpdate,
			templateCreate,
			templateCreateProceed,
			templateCreateCancel,
			templateCreatedName,
			isTemplateCreating,
      textPositions,
      isAlignmentDisabled,
      alignOptions,
      textAlign,
			loading,
			optionActive,
			optionSet,
			
			isTemplateChangesTracked,
			templateRelatedOccupied,
			isTemplateRelatedOccupied,
			
			isPopupMediaActive,
			popupMediaOpen,
			popupMediaClose,
			mediaChosenGet,
		}
	}
}
</script>

<style lang="scss">

</style>
